import { Button } from '@forwardflow/ui-v2'
import { useState, useEffect, useMemo } from 'react'
import { useOrganizationDetails } from '@forwardflow/queries/organization.queries'
import OrganizationService from '@forwardflow/services/OrganizationService'
import { message } from 'antd'
import {
   AutomationsForm,
   IAutomationsFormValue,
   automationsConfigToFormValue,
   formValueToAutomationsPayload,
   validateAutomationsForm,
} from '@forwardflow/modules/settings/Automations/AutomationsForm'
import SayHiSection from '@forwardflow/modules/settings/Automations/SayHiSection'

const Automations = () => {
   const { data, mutate } = useOrganizationDetails({})
   const [formData, setFormData] = useState<IAutomationsFormValue>(
      automationsConfigToFormValue(null)
   )
   const [isSaving, setIsSaving] = useState(false)

   // The last-saved form value — the baseline we diff against to know if
   // anything has been touched.
   const baseline = useMemo(
      () => automationsConfigToFormValue(data?.automations ?? null),
      [data]
   )

   // Reset the form to the loaded config whenever it (re)loads / is saved.
   useEffect(() => {
      if (data?.automations !== undefined) {
         setFormData(automationsConfigToFormValue(data.automations))
      }
   }, [data])

   const isDirty = useMemo(
      () => JSON.stringify(formData) !== JSON.stringify(baseline),
      [formData, baseline]
   )

   const handleDiscard = () => setFormData(baseline)

   const handleSave = async () => {
      const validationError = validateAutomationsForm(formData)
      if (validationError) {
         message.error(validationError)
         return
      }

      setIsSaving(true)
      try {
         const payload = formValueToAutomationsPayload(formData)
         await OrganizationService.updateAutomations(payload)
         message.success('Aria settings updated')
         mutate()
      } catch {
         message.error('Failed to update automations')
      } finally {
         setIsSaving(false)
      }
   }

   return (
      <div className="flex flex-col flex-1 h-full overflow-hidden">
         <div className="flex items-center justify-between h-20 pl-8 pr-4 bg-white/5 shrink-0">
            <span className="text-lg font-medium">Aria</span>
            {isDirty && (
               <div className="flex gap-3">
                  <Button
                     onClick={handleDiscard}
                     disabled={isSaving}
                     className="!h-10 !rounded px-7 !mt-0 max-w-40 bg-white/10 border-primary/10 hover:border-primary/10 hover:bg-white/20"
                  >
                     Discard
                  </Button>
                  <Button
                     onClick={handleSave}
                     loading={isSaving}
                     className="!h-10 !rounded px-7 !mt-0 max-w-40"
                  >
                     Save
                  </Button>
               </div>
            )}
         </div>
         <div className="flex-1 overflow-y-auto">
            <AutomationsForm
               value={formData}
               disabled={isSaving}
               onChange={setFormData}
            />
            <div className='px-6 pb-6'>
               <SayHiSection emails={formData.recipientEmails} />
            </div>
         </div>
      </div>
   )
}

export default Automations
